<template>
  <el-container direction="vertical" style="margin: 5vh 5vw">
    <environment-new :isVisible.sync="showNewModal" />
    <environment-edit
      :isVisible.sync="showEditModal"
      :envSelected="envSelected"
    />
    <div class="header" style="margin-bottom: 25px">
      <h3>
        Environments
        <el-tooltip placement="right" effect="light">
          <span slot="content"
            >Environments are used to access your Planning Analytics data
            within<br />DataMaestro, where each environment represents a
            Planning Analytics host<br />server.
            {{
              userRole === "Administrator"
                ? "Add a new environment or select an existing one to get started."
                : "Select an environment to get started."
            }}
          </span>
          <i
            class="el-icon-info"
            style="position: relative; left: 2px; bottom: 12px; font-size: 1rem"
          />
        </el-tooltip>
      </h3>
    </div>
    <el-table
      :data="items"
      @current-change="selectedEnv"
      :default-sort="{ prop: 'name', order: 'ascending' }"
      style="border: 2px solid #ebeef5; width: 100%"
      row-class-name="body-row"
    >
      <el-table-column v-if="userRole === 'Administrator'" width="200">
        <template slot="header">
          <el-button
            class="button"
            @click="showNewModal = true"
            icon="el-icon-plus"
            >Add Environment</el-button
          >
        </template>
        <template slot-scope="scope">
          <el-button-group>
            <el-tooltip placement="bottom" content="Edit Environment">
              <el-button
                size="medium"
                class="table-button"
                icon="el-icon-edit"
                @click="
                  envSelected = scope.row;
                  showEditModal = !showEditModal;
                "
                @click.stop
            /></el-tooltip>
            <el-tooltip placement="bottom" content="Delete Environment">
              <el-button
                size="medium"
                class="table-button"
                @click="deleteEnv(scope.row)"
                @click.stop
                icon="el-icon-delete"
            /></el-tooltip>
          </el-button-group>
        </template>
      </el-table-column>
      <el-table-column
        prop="name"
        label="Friendly Name"
        sortable
        :min-width="40"
      />
      <el-table-column
        prop="adminHost"
        label="Admin Host or IBM Environment"
        sortable
        :min-width="40"
      />
      <el-table-column
        prop="isCloud"
        label="Cloud Environment"
        sortable
        :min-width="20"
      />
    </el-table>
  </el-container>
</template>

<script>
import EnvironmentService from "../services/EnvironmentService";
import EnvironmentEdit from "../components/modals/EnvironmentEdit";
import EnvironmentNew from "../components/modals/EnvironmentNew";
import { mapState } from "vuex";
import Helper from "../utilities/Helper";
import AuthenticationService from "../services/AuthenticationService";

/**
 * @vue-data {Object} [envSelected=null] - Environment object to be updated
 * @vue-data {String} [error=""] - Error message to display
 * @vue-data {Array} [items=[]] - Current list of environments
 * @vue-data {String} [sortBy="name"] - Current field to sort by
 */
export default {
  name: "Environment",
  data() {
    return {
      envSelected: null,
      items: [],
      sortBy: "name",
      checkValid: false,
      showNewModal: false,
      showEditModal: false,
    };
  },
  watch: {
    showEditModal(value) {
      if (!value) this.getEnv();
    },
    showNewModal(value) {
      if (!value) this.getEnv();
    },
  },
  components: { EnvironmentEdit, EnvironmentNew },
  computed: {
    ...mapState([
      "environmentId",
      "userId",
      "isUserLoggedIn",
      "organizationId",
      "userRole",
      "instanceId",
    ]),
  },
  methods: {
    handleError: Helper.handleError,
    /**
     * Logs the user out of the PA server
     * @function
     */
    async logoutPA() {
      const loading = this.$loading({
        lock: true,
        //text: "Working",
        background: "rgba(0, 0, 0, 0.3)",
      });
      try {
        if (this.instanceId) {
          await AuthenticationService.logout({
            instanceId: this.instanceId,
          });
          this.$store.dispatch("logoutPA");
        }
      } catch (err) {
        this.handleError(err, this, this.instanceId);
      } finally {
        loading.close();
      }
    },
    /**
     * Selects environment to open
     * @function
     */
    selectedEnv(item) {
      this.$store.dispatch("setEnvName", item.name);
      this.$store.dispatch("setEnvAdminHost", item.adminHost);
      this.$store.dispatch("setEnvironmentId", item.id);
      this.$store.dispatch("setEnvRights", item.rights);
      this.$store.dispatch(
        "setEnvType",
        item.isSaaS ? "SaaS" : item.isCloud === "Yes" ? "cloud" : "local"
      );
      this.$router.push({ name: "servers" });
    },
    /**
     * Deletes an environment
     * @function
     */
    async deleteEnv(item) {
      const input = prompt(
        `Are you sure you want to delete the "${item.name}" environment? All tasks and packages associated with this environment will be permanently deleted. Type the name of the environment to confirm.`
      );
      if (input === null) {
        return;
      } else if (input.toLowerCase() === item.name.toLowerCase()) {
        const loading = this.$loading({
          lock: true,
          //text: "Working",
          background: "rgba(0, 0, 0, 0.3)",
        });
        try {
          await EnvironmentService.delete({
            environmentId: item.id,
          });
          this.getEnv();
          this.$message({
            type: "success",
            message: `"${item.name}" environment deleted successfully`,
            duration: 5000,
            showClose: true,
          });
        } catch (err) {
          this.handleError(err, this, this.instanceId);
        } finally {
          loading.close();
        }
      } else {
        this.$message({
          type: "warning",
          message: "You must provide valid input to delete the environment.",
          duration: 7000,
          showClose: true,
        });
      }
    },
    /**
     * Fetches current list of environments
     * @function
     */
    async getEnv() {
      const loading = this.$loading({
        lock: true,
        //text: "Working",
        background: "rgba(0, 0, 0, 0.3)",
      });
      try {
        const response = await EnvironmentService.show({
          userId: this.userId,
        });
        this.items = response.data.environments;
      } catch (err) {
        this.handleError(err, this, this.instanceId);
      } finally {
        loading.close();
      }
    },
  },
  async mounted() {
    if (!this.isUserLoggedIn) {
      this.$router.push({ name: "login" });
    } else {
      this.logoutPA(this, this.instanceId);
      this.$store.dispatch("resetEnvironment");
      await this.getEnv();
      if (this.isUserLoggedIn) {
        this.$message({
          type: "info",
          message:
            "Choose which Planning Analytics environment you want to work with.",
          duration: 5000,
          showClose: true,
        });
      }
    }
  },
};
</script>
<style scoped>
.header {
  display: flex;
  justify-content: space-between;
}
.error :deep(.el-input__inner) {
  border: 1px solid red;
  border-radius: 4px;
}
.button {
  background-color: #b3282d;
  color: white;
  border-color: white;
  padding: 10px 15px;
}
.button:focus {
  background-color: #9a0f14;
  color: white;
  border-color: white;
  outline: none;
}
.button:hover {
  background-color: #9a0f14;
  color: white;
  border-color: white;
  outline: none;
}
.table-button {
  background-color: inherit;
  color: #606266;
  border: none;
  outline: none;
  font-size: 1.15rem;
}
.table-button:focus {
  background-color: inherit;
  color: #606266;
  border: none;
  outline: none;
}
.table-button:hover {
  background-color: #ecf5fe;
  color: #606266;
  border: none;
  outline: none;
}
</style>
